import { useState, useEffect, useRef, useMemo } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import AdUnit from './AdUnit.jsx'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  EyeIcon,
} from '@heroicons/react/20/solid'
import { supabase } from '../lib/supabase'
import DocumentMeta from './DocumentMeta.jsx'
import DocumentViewerModal from './DocumentViewerModal.jsx'
import { trackEvent } from './lib/analytics'
import { reportError } from './lib/reportError'

import { MIN_YEAR, THEMATA_YEAR } from './constants.js'

function ExamsPage() {
  const location = useLocation()
  const { year: yearParam } = useParams()
  const navigate = useNavigate()

  // /themata/ola may or may not provide :year depending on router setup
  const isAll = yearParam === 'ola' || location.pathname === '/themata/ola'

  const initialYear = isAll ? '' : Number(yearParam || THEMATA_YEAR)

  const validYear =
    isAll ||
    (Number.isFinite(initialYear) && initialYear >= MIN_YEAR && initialYear <= THEMATA_YEAR)

  const metaYear = isAll ? '2018-2025' : String(initialYear)

  const SUBJECTS = [
    { slug: 'language', label: 'Νεοελληνική Γλώσσα - Έκθεση & Λογοτεχνία' },
    { slug: 'ancient', label: 'Αρχαία' },
    { slug: 'history', label: 'Ιστορία' },
    { slug: 'latin', label: 'Λατινικά' },
    { slug: 'math', label: 'Μαθηματικά' },
    { slug: 'physics', label: 'Φυσική' },
    { slug: 'chemistry', label: 'Χημεία' },
    { slug: 'biology', label: 'Βιολογία' },
    { slug: 'economics', label: 'Οικονομία' },
    { slug: 'informatics', label: 'Πληροφορική' },

    // special
    { slug: 'english', label: 'Αγγλικά (Ειδικό)' },
    { slug: 'german', label: 'Γερμανικά (Ειδικό)' },
    { slug: 'french', label: 'Γαλλικά (Ειδικό)' },
    { slug: 'italian', label: 'Ιταλικά (Ειδικό)' },
    { slug: 'spanish', label: 'Ισπανικά (Ειδικό)' },
    { slug: 'free_design', label: 'Ελεύθερο Σχέδιο (Ειδικό)' },
    { slug: 'linear_design', label: 'Γραμμικό Σχέδιο (Ειδικό)' },
  ]

  const [filters, setFilters] = useState({
    year: THEMATA_YEAR,
    field: '',
    subject: '',
  })

  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)
  const [totalResults, setTotalResults] = useState(0)

  const resultsPerPage = 15
  const lastPage = Math.max(1, Math.ceil(totalResults / resultsPerPage))

  // Mobile filter sheet
  const [showFilters, setShowFilters] = useState(false)

  // In-app document viewer
  const [viewerDoc, setViewerDoc] = useState(null)

  const openViewer = (item, type) => {
    const url =
      type === 'exam'
        ? item.exam_url
        : type === 'solution'
        ? item.solution_url
        : item.secondary_url
    if (!url) return

    trackEvent('exam_file_opened', {
      type,
      view: 'modal',
      year: item.year,
      subject_slug: item.subject_slug || null,
      subject_id: item.subject_id || null,
    })

    const titleByType = {
      exam: 'Θέματα',
      solution: 'Λύσεις ΟΕΦΕ',
      secondary: item.secondary_label || 'Αρχείο',
    }

    setViewerDoc({
      url,
      kind: type,
      title: item.subject_label,
      subtitle: `${titleByType[type]} • ${item.year}`,
    })
  }

  // Prevent duplicate fetches (esp. StrictMode dev)
  const lastFetchKeyRef = useRef('')

  // If invalid year, redirect to latest year route
  useEffect(() => {
    if (!validYear) {
      navigate(`/themata/${THEMATA_YEAR}`, { replace: true })
    }
  }, [validYear, navigate])

  const headingText = useMemo(() => {
    if (isAll) return 'Θέματα Πανελληνίων'
    return `Θέματα Πανελληνίων ${initialYear}`
  }, [isAll, initialYear])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  const buildPublicUrl = (path) => {
    if (!path) return ''
    const { data } = supabase.storage.from('exams').getPublicUrl(path)
    return data?.publicUrl || ''
  }

  const goToSubjectYear = (row) => {
    if (!row?.subject_slug || !row?.year) return
  
    trackEvent('exam_subject_year_opened', {
      subject_slug: row.subject_slug,
      subject_id: row.subject_id || null,
      year: row.year,
      from: `${location.pathname}${location.search}`,
    })
  
    navigate(`/themata/${row.subject_slug}/${row.year}`, {
      state: { from: `${location.pathname}${location.search}` },
    })
  }  

  const fetchSubjectIdsByFieldNumber = async (fieldNumber) => {
    const { data, error } = await supabase
      .from('subject_fields')
      .select('subject_id, fields!inner(number)')
      .eq('fields.number', Number(fieldNumber))

    if (error) {
      console.error('Error fetching subject_ids by field', error)
      reportError(error, { where: 'fetchSubjectIdsByFieldNumber', fieldNumber })
      return []
    }

    return Array.from(new Set((data || []).map((r) => r.subject_id)))
  }

  const fetchSubjectIdBySlug = async (slug) => {
    const { data, error } = await supabase
      .from('subjects')
      .select('id')
      .eq('slug', slug)
      .maybeSingle()

    if (error) {
      console.error('Error fetching subject id by slug', error)
      reportError(error, { where: 'fetchSubjectIdBySlug', slug })
      return null
    }
    return data?.id || null
  }

  const handleSubmit = async (e, newPage = 1, filtersOverride = null) => {
    if (e) e.preventDefault()
    const currentFilters = filtersOverride || filters

    setLoading(true)
    setPage(newPage)

    if (newPage !== page) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }

    try {
      const from = (newPage - 1) * resultsPerPage
      const to = from + resultsPerPage - 1

      // If subject selected, resolve to subject_id
      let subjectIdForSlug = null
      if (currentFilters.subject) {
        subjectIdForSlug = await fetchSubjectIdBySlug(currentFilters.subject)
        if (!subjectIdForSlug) {
          setResults([])
          setTotalResults(0)
          setHasPrev(newPage > 1)
          setHasNext(false)
          return
        }
      }

      // Field filter: precompute matching subject_ids
      let subjectIdsForField = null
      if (currentFilters.field) {
        subjectIdsForField = await fetchSubjectIdsByFieldNumber(currentFilters.field)
        if (!subjectIdsForField.length) {
          setResults([])
          setTotalResults(0)
          setHasPrev(newPage > 1)
          setHasNext(false)
          return
        }
      }

      // If both subject and field selected, ensure subject belongs to that field
      if (subjectIdForSlug && subjectIdsForField && !subjectIdsForField.includes(subjectIdForSlug)) {
        setResults([])
        setTotalResults(0)
        setHasPrev(newPage > 1)
        setHasNext(false)
        return
      }

      let query = supabase
        .from('exams')
        .select(
          `
          id,
          year,
          subject_id,
          exam_path,
          solution_path,
          secondary_path,
          subjects:subject_id (
            id,
            name,
            slug
          )
        `,
          { count: 'exact' }
        )
        .order('year', { ascending: false })
        .order('created_on', { ascending: true })
        .range(from, to)

      // Year filter
      if (currentFilters.year) {
        query = query.eq('year', Number(currentFilters.year))
      } else {
        query = query.gte('year', MIN_YEAR).lte('year', THEMATA_YEAR)
      }

      // Subject filter (by subject_id)
      if (subjectIdForSlug) {
        query = query.eq('subject_id', subjectIdForSlug)
      }

      // Field filter (by subject_id IN ...)
      if (subjectIdsForField && !subjectIdForSlug) {
        query = query.in('subject_id', subjectIdsForField)
      }

      const { data, error, count } = await query

      if (error) {
        console.error('Error fetching exams', error)
        reportError(error, { where: 'fetch exams', filters: currentFilters })
        setResults([])
        setTotalResults(0)
        setHasPrev(newPage > 1)
        setHasNext(false)
        return
      }

      // Build fields map for display (no per-row queries)
      const uniqueSubjectIds = Array.from(
        new Set((data || []).map((r) => r.subject_id).filter(Boolean))
      )

      const subjectFieldsMap = new Map()
      if (uniqueSubjectIds.length) {
        const { data: sfData, error: sfError } = await supabase
          .from('subject_fields')
          .select('subject_id, fields!inner(number)')
          .in('subject_id', uniqueSubjectIds)

        if (sfError) {
          console.error('Error fetching subject fields map', sfError)
          reportError(sfError, {
            where: 'fetch subject fields map',
            uniqueSubjectIdsCount: uniqueSubjectIds.length,
          })
        } else {
          for (const row of sfData || []) {
            const sid = row.subject_id
            const num = row?.fields?.number
            if (!sid || typeof num !== 'number') continue
            if (!subjectFieldsMap.has(sid)) subjectFieldsMap.set(sid, new Set())
            subjectFieldsMap.get(sid).add(num)
          }
        }
      }

      const mapped = (data || []).map((row) => {
        const subjectSlug = row?.subjects?.slug || ''
        const subjectLabelFromList = SUBJECTS.find((s) => s.slug === subjectSlug)?.label
        const subjectName = subjectLabelFromList || row?.subjects?.name || subjectSlug || 'Μάθημα'

        const getSecondaryLabel = (slug) => {
          if (slug === 'free_design') return 'Οδηγίες'
          if (slug === 'linear_design') return 'Σχέδιο'
          return ''
        }

        const fieldsSet = subjectFieldsMap.get(row.subject_id) || new Set()
        const fieldsArr = Array.from(fieldsSet).sort((a, b) => a - b)

        return {
          id: row.id,
          year: row.year,
          subject_id: row.subject_id,
          subject_slug: subjectSlug,
          subject_label: subjectName,
          subject_fields: fieldsArr,
          exam_url: buildPublicUrl(row.exam_path),
          secondary_url: buildPublicUrl(row.secondary_path),
          secondary_label: getSecondaryLabel(subjectSlug),
          // solution_url: buildPublicUrl(row.solution_path),
          solution_url: row.solution_path,
        }
      })

      const total = count || 0
      setTotalResults(total)
      setResults(mapped)
      setHasPrev(newPage > 1)
      setHasNext(newPage * resultsPerPage < total)
    } finally {
      setLoading(false)
    }
  }

  // ✅ Sync filters from route:
  // - year from /themata/:year or /themata/ola
  // - subject/field from query: ?subject=...&field=...
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const subjectFromUrl = params.get('subject') || ''
    const fieldFromUrl = params.get('field') || ''

    const next = {
      year: isAll ? '' : initialYear,
      subject: subjectFromUrl,
      field: fieldFromUrl,
    }

    setFilters(next)

    const fetchKey = `${next.year}|${next.subject}|${next.field}`
    if (lastFetchKeyRef.current === fetchKey) return
    lastFetchKeyRef.current = fetchKey

    handleSubmit(null, 1, next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearParam, isAll, location.search])

  // SUBMIT = update URL to /themata/<year> or /themata/ola + keep subject/field in query
  const submitAndSyncUrl = (e) => {
    if (e) e.preventDefault()

    const yearPart = filters.year ? String(filters.year) : 'ola'
    const nextPath = `/themata/${yearPart}`

    const qs = new URLSearchParams()
    if (filters.subject) qs.set('subject', filters.subject)
    if (filters.field) qs.set('field', filters.field)

    const nextFull = `${nextPath}${qs.toString() ? `?${qs.toString()}` : ''}`

    // navigate even if same path to ensure search updates
    if (location.pathname === nextPath && location.search === (qs.toString() ? `?${qs.toString()}` : '')) {
      handleSubmit(null, 1, filters)
      return
    }

    navigate(nextFull)
  }

  return (
    <>
      <DocumentMeta
        title={
          isAll
            ? 'Θέματα Πανελληνίων 2018-2025 | Πανελλήνιες+'
            : `Θέματα Πανελληνίων ${metaYear} | Πανελλήνιες+`
        }
        description={
          isAll
            ? 'Όλα τα θέματα Πανελληνίων (2018-2025) με φίλτρα ανά μάθημα, πεδίο και έτος.'
            : `Θέματα Πανελληνίων ${metaYear} με φίλτρα ανά μάθημα και πεδίο. Κατέβασε θέματα για εξάσκηση.`
        }
        canonical={`https://www.panellhniesplus.gr${location.pathname}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Αρχική", item: "https://www.panellhniesplus.gr/" },
          { "@type": "ListItem", position: 2, name: "Θέματα", item: "https://www.panellhniesplus.gr/themata" },
          { "@type": "ListItem", position: 3, name: isAll ? "Θέματα 2018-2025" : `Θέματα ${metaYear}`, item: `https://www.panellhniesplus.gr${location.pathname}` },
        ],
      }) }} />

      <div className="min-h-screen bg-white text-gray-900">
        <Header />

        {/* Mobile controls bar */}
        <div className="md:hidden sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowFilters(true)}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 cursor-pointer"
            >
              Φίλτρα
            </button>

            <div className="flex items-center gap-2">
              <label htmlFor="year-m" className="text-sm font-medium text-gray-700">
                Έτος
              </label>
              <select
                id="year-m"
                name="year"
                value={filters.year}
                onChange={(e) => {
                  const newYear = e.target.value
                  setFilters((prev) => ({ ...prev, year: newYear }))
                  // build next path from the year ('' => ola)
                  const yearPart = newYear ? String(newYear) : 'ola'
                  const nextPath = `/themata/${yearPart}`

                  // keep any existing filters in the URL (subject/field)
                  const qs = new URLSearchParams()
                  if (filters.subject) qs.set('subject', filters.subject)
                  if (filters.field) qs.set('field', filters.field)

                  const nextSearch = qs.toString() ? `?${qs.toString()}` : ''
                  const nextFull = `${nextPath}${nextSearch}`

                  // if URL is already the same, just fetch immediately
                  if (location.pathname === nextPath && location.search === nextSearch) {
                    handleSubmit(null, 1, { ...filters, year: newYear })
                    return
                  }
                  // otherwise, navigate — your route-sync useEffect will fetch
                  navigate(nextFull)
                }}
                className="cursor-pointer border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-800 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
              >
                <option value="">Όλα</option>
                {Array.from({ length: THEMATA_YEAR - MIN_YEAR + 1 }, (_, i) => {
                  const year = THEMATA_YEAR - i
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  )
                })}
              </select>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Filters Sidebar (desktop only) */}
            <aside className="hidden md:block md:w-[320px]">
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-100">
                  <h2 className="text-lg font-bold text-gray-900">Φίλτρα</h2>
                  <p className="text-sm text-gray-500 mt-1">Βρες θέματα ανά έτος, μάθημα ή πεδίο</p>
                </div>

                <form className="p-6 space-y-5" onSubmit={submitAndSyncUrl}>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Έτος</label>
                    <select
                      name="year"
                      value={filters.year}
                      onChange={handleChange}
                      className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                    >
                      <option value="">Όλα</option>
                      {Array.from({ length: THEMATA_YEAR - MIN_YEAR + 1 }, (_, i) => {
                        const y = THEMATA_YEAR - i
                        return (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        )
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Μάθημα</label>
                    <select
                      name="subject"
                      value={filters.subject || ''}
                      onChange={handleChange}
                      className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                    >
                      <option value="">Όλα</option>
                      {SUBJECTS.map((s) => (
                        <option key={s.slug} value={s.slug}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Πεδίο</label>
                    <select
                      name="field"
                      value={filters.field}
                      onChange={handleChange}
                      className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                    >
                      <option value="">Όλα</option>
                      <option value="1">Πεδίο 1</option>
                      <option value="2">Πεδίο 2</option>
                      <option value="3">Πεδίο 3</option>
                      <option value="4">Πεδίο 4</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="w-full cursor-pointer bg-gray-900 text-white py-3 rounded-xl font-semibold hover:bg-gray-800 transition"
                  >
                    Αναζήτηση
                  </button>
                </form>
              </div>
            </aside>

            {/* Results Section */}
            <main className="flex-1 min-w-0">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-6">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
                    {headingText}
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">
                    Δες και κατέβασε θέματα Πανελληνίων για εξάσκηση.
                  </p>
                </div>

                <div className="hidden md:flex items-center gap-3">
                  <div className="text-sm text-gray-600">
                    Σύνολο: <span className="font-semibold text-gray-900">{totalResults}</span>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-9 w-9 border-t-2 border-b-2 border-gray-500" />
                </div>
              ) : results.length > 0 ? (
                <>
                  <div className="md:hidden mb-4 text-sm text-gray-600">
                    Σύνολο: <span className="font-semibold text-gray-900">{totalResults}</span>
                  </div>

                  <ul className="space-y-3">
                    {results.map((item) => (
                      <li
                        key={item.id}
                        className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="min-w-0">
                          <button
                            type="button"
                            onClick={() => goToSubjectYear(item)}
                            className="cursor-pointer text-left font-extrabold text-gray-900 leading-snug hover:underline underline-offset-4"
                          >
                            {item.subject_label}
                          </button>
                            <p className="text-sm text-gray-500 mt-1">{item.year}</p>

                            {item.subject_fields?.length ? (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {item.subject_fields.map((n) => (
                                  <span
                                    key={n}
                                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200"
                                  >
                                    Πεδίο {n}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>

                          <div className="flex flex-wrap gap-2 sm:justify-end">
                            {item.exam_url && (
                              <button
                                type="button"
                                onClick={() => openViewer(item, 'exam')}
                                className="inline-flex items-center justify-center gap-1.5 cursor-pointer bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition"
                              >
                                <EyeIcon className="w-4 h-4" />
                                Θέματα
                              </button>
                            )}

                            {item.secondary_url && (
                              <button
                                type="button"
                                onClick={() => openViewer(item, 'secondary')}
                                className="inline-flex items-center justify-center gap-1.5 cursor-pointer bg-white text-gray-900 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-300 hover:bg-gray-50 transition"
                              >
                                <EyeIcon className="w-4 h-4" />
                                {item.secondary_label}
                              </button>
                            )}

                            {item.solution_url && (
                              <button
                                type="button"
                                onClick={() => openViewer(item, 'solution')}
                                className="inline-flex items-center justify-center gap-1.5 cursor-pointer bg-white text-gray-900 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-300 hover:bg-gray-50 transition"
                              >
                                <EyeIcon className="w-4 h-4" />
                                Λύσεις ΟΕΦΕ
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap justify-center items-center gap-2">
                    <button
                      onClick={() => handleSubmit(null, 1)}
                      disabled={page === 1}
                      className={`hidden sm:inline-flex items-center gap-1 px-4 py-2.5 rounded-xl border text-sm font-semibold transition ${
                        page === 1
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <ChevronDoubleLeftIcon className="w-4 h-4" />
                      Πρώτη
                    </button>

                    <button
                      onClick={() => handleSubmit(null, page - 1)}
                      disabled={!hasPrev}
                      className={`flex items-center gap-1 px-4 py-2.5 rounded-xl border text-sm font-semibold transition ${
                        !hasPrev
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                      Προηγούμενη
                    </button>

                    <span className="px-4 py-2.5 rounded-xl text-sm font-bold text-gray-800 bg-gray-50 border border-gray-200">
                      Σελίδα {page} / {lastPage}
                    </span>

                    <button
                      onClick={() => handleSubmit(null, page + 1)}
                      disabled={!hasNext}
                      className={`flex items-center gap-1 px-4 py-2.5 rounded-xl border text-sm font-semibold transition ${
                        !hasNext
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      Επόμενη
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleSubmit(null, lastPage)}
                      disabled={page >= lastPage}
                      className={`hidden sm:inline-flex items-center gap-1 px-4 py-2.5 rounded-xl border text-sm font-semibold transition ${
                        page >= lastPage
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      Τελευταία
                      <ChevronDoubleRightIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* In-content ad — below the results list */}
                  <AdUnit />
                </>
              ) : (
                <div className="text-center text-gray-500 py-16">
                  <div className="mx-auto max-w-md border border-gray-200 rounded-2xl bg-gray-50 p-6">
                    <p className="font-semibold text-gray-900">Δεν βρέθηκαν αποτελέσματα</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Δοκίμασε να αλλάξεις φίλτρα (έτος, μάθημα ή πεδίο).
                    </p>
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>

        {/* Mobile Filters Sheet */}
        {showFilters && (
          <div className="fixed inset-0 z-[1000] md:hidden">
            <div
              className="absolute inset-0 bg-black/50 cursor-pointer"
              onClick={() => setShowFilters(false)}
              aria-hidden="true"
            />
            <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl border-t border-gray-200 max-h-[85vh] flex flex-col animate-[sheetIn_.18s_ease-out]">
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <div className="flex-1 flex justify-center">
                  <span className="block h-1.5 w-12 bg-gray-300 rounded-full" />
                </div>
              </div>

              <div className="overflow-y-auto px-4 pb-4 flex-1">
                <div className="pb-2">
                  <h3 className="text-base font-bold text-gray-900">Φίλτρα</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Εφάρμοσε φίλτρα και πάτα αναζήτηση</p>
                </div>

                <form
                  className="space-y-5"
                  onSubmit={(e) => {
                    submitAndSyncUrl(e)
                    setShowFilters(false)
                  }}
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Έτος</label>
                    <select
                      name="year"
                      value={filters.year}
                      onChange={handleChange}
                      className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                    >
                      <option value="">Όλα</option>
                      {Array.from({ length: THEMATA_YEAR - MIN_YEAR + 1 }, (_, i) => {
                        const y = THEMATA_YEAR - i
                        return (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        )
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Μάθημα</label>
                    <select
                      name="subject"
                      value={filters.subject || ''}
                      onChange={handleChange}
                      className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                    >
                      <option value="">Όλα</option>
                      {SUBJECTS.map((s) => (
                        <option key={s.slug} value={s.slug}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Πεδίο</label>
                    <select
                      name="field"
                      value={filters.field}
                      onChange={handleChange}
                      className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                    >
                      <option value="">Όλα</option>
                      <option value="1">Πεδίο 1</option>
                      <option value="2">Πεδίο 2</option>
                      <option value="3">Πεδίο 3</option>
                      <option value="4">Πεδίο 4</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="w-full cursor-pointer bg-gray-900 text-white py-3 rounded-xl font-semibold hover:bg-gray-800 transition"
                  >
                    Αναζήτηση
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowFilters(false)}
                    className="w-full cursor-pointer bg-white text-gray-900 py-3 rounded-xl font-semibold border border-gray-300 hover:bg-gray-50 transition"
                  >
                    Κλείσιμο
                  </button>
                </form>
              </div>
            </div>

            <style>{`
              @keyframes sheetIn {
                from { transform: translateY(12%); opacity: .7; }
                to   { transform: translateY(0);   opacity: 1;  }
              }
              @media (prefers-reduced-motion: reduce) {
                .animate-[sheetIn_.18s_ease-out] { animation: none; }
              }
            `}</style>
          </div>
        )}

        <Footer />
      </div>

      <DocumentViewerModal
        open={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        url={viewerDoc?.url}
        title={viewerDoc?.title}
        subtitle={viewerDoc?.subtitle}
        kind={viewerDoc?.kind}
      />
    </>
  )
}

export default ExamsPage
